// src/components/ui/Header.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Icon from "../AppIcon";
import Button from "./Button";
import Select from "./Select";
import { useAuth } from "../../auth/AuthProvider";
import api, { getAccessToken } from "../../lib/api";
import { SUPPORTED_LANGUAGES } from "../../i18n";
import {
  subscribeTheme,
  toggleThemePreference,
  getThemePreference,
  getResolvedTheme,
} from "../../lib/theme";
import logoLight from "../../assets/logo-light.webp";
import logoDark from "../../assets/logo-dark.webp";
import UnresolvedAppointmentsGate from "../UnresolvedAppointmentsGate";

const LS_LOCATION_SCOPE_KEY = "current_location_scope_id";
const LS_LOCATION_ID_KEY = "current_location_id";
const NOTIF_SOUND_URL = "/sounds/notificare.wav";
const LS_NOTIF_SOUND_ENABLED = "notif_sound_enabled";

const Header = ({ isCollapsed = false, onToggleSidebar }) => {
  const { user, roles, logout, permissions, currentBusinessId } = useAuth();
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  const displayName = useMemo(() => {
    return (
      user?.name ||
      [user?.first_name, user?.last_name].filter(Boolean).join(" ") ||
      user?.email ||
      "Utilizator"
    );
  }, [user]);

  const primaryRole = useMemo(() => {
    return Array.isArray(roles) && roles.length ? roles[0] : "Utilizator";
  }, [roles]);

  const displayEmail = user?.email || "";

  const [showUserMenu, setShowUserMenu] = useState(false);

  /* Notifications (in-app) */
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifItems, setNotifItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifLoading, setNotifLoading] = useState(false);

  /* Notifications sound */
  const [soundEnabled, setSoundEnabled] = useState(() => {
    try {
      return window.localStorage.getItem(LS_NOTIF_SOUND_ENABLED) === "1";
    } catch {
      return false;
    }
  });
  const notifAudioRef = useRef(null);
  const unreadBootRef = useRef(false);
  const prevUnreadRef = useRef(0);

  // Theme (light/dark/system)
  const [themePref, setThemePref] = useState(() => getThemePreference());
  const [resolvedTheme, setResolvedTheme] = useState(() => getResolvedTheme());

  useEffect(() => {
    return subscribeTheme(({ preference, resolved }) => {
      setThemePref(preference);
      setResolvedTheme(resolved);
    });
  }, []);

  const isPureEmployee = permissions?.isPureEmployee;
  const canManageBusinessSettings = permissions?.canManageBusinessSettings;

  const isOwnerLike = permissions?.isSuperAdmin || permissions?.isOwnerCurrentBusiness;

  // Location scope (owner only)
  const [locationScope, setLocationScope] = useState(() => {
    if (typeof window === "undefined") return "all";
    const raw = window.localStorage.getItem(LS_LOCATION_SCOPE_KEY);
    return raw ? String(raw) : "all";
  });
  const [locationOptions, setLocationOptions] = useState([
    { value: "all", label: "Toate sucursalele" },
  ]);
  const [locationsLoading, setLocationsLoading] = useState(false);

  const userMenuRef = useRef(null);
  const notifMenuRef = useRef(null);

  /* =========================
     SOUND (lazy, no preload)
  ========================= */

  const buildNotifSoundUrl = () => {
    try {
      const raw = (NOTIF_SOUND_URL || "").trim();
      if (!raw) return null;
      return new URL(raw, window.location.origin).toString();
    } catch {
      return null;
    }
  };

  useEffect(() => {
    // IMPORTANT: nu setăm src aici -> nu se face load automat (și nu mai poluăm consola)
    try {
      const a = new Audio();
      a.preload = "none";
      a.volume = 0.9;

      try {
        a.setAttribute?.("playsinline", "true");
      } catch {}

      a.addEventListener("error", () => {
        try {
          a.pause();
          a.removeAttribute?.("src");
          a.load?.();
        } catch {}
        notifAudioRef.current = null;
      });

      notifAudioRef.current = a;
    } catch {
      notifAudioRef.current = null;
    }

    return () => {
      try {
        const a = notifAudioRef.current;
        if (a) {
          a.pause();
          a.removeAttribute?.("src");
          a.load?.();
          notifAudioRef.current = null;
        }
      } catch {}
    };
  }, []);

  const ensureNotifAudioReady = () => {
    const a = notifAudioRef.current;
    if (!a) return null;

    const url = buildNotifSoundUrl();
    if (!url) return null;

    // setăm src doar când chiar avem nevoie
    if (a.src !== url) {
      a.src = url;
      try {
        a.load?.();
      } catch {}
    }

    return a;
  };

  const primeNotifSound = async () => {
    // must be called from a user gesture (mobile autoplay policy)
    try {
      const a = ensureNotifAudioReady();
      if (!a) return;

      a.currentTime = 0;
      const prevVol = a.volume;
      a.volume = 0;

      await a.play();
      a.pause();
      a.currentTime = 0;
      a.volume = prevVol;
    } catch {}
  };

  const playNotifSound = async () => {
    try {
      const a = ensureNotifAudioReady();
      if (!a) return;

      a.pause();
      a.currentTime = 0;
      await a.play();
    } catch {}
  };

  const toggleNotifSound = async () => {
    const next = !soundEnabled;
    setSoundEnabled(next);
    try {
      window.localStorage.setItem(LS_NOTIF_SOUND_ENABLED, next ? "1" : "0");
    } catch {}
    if (next) await primeNotifSound();
  };

  /* =========================
     Locations (owner scope)
  ========================= */
  useEffect(() => {
    if (typeof window === "undefined") return;

    // Dacă userul nu e owner, nu păstrăm scope (ca să nu afecteze accidental UI-ul)
    if (!isOwnerLike) {
      window.localStorage.removeItem(LS_LOCATION_SCOPE_KEY);
      setLocationScope("all");
      setLocationOptions([{ value: "all", label: "Toate sucursalele" }]);
      return;
    }

    const bid = currentBusinessId || window.localStorage.getItem("current_business_id");
    if (!bid) return;

    let cancelled = false;
    setLocationsLoading(true);

    api
      .get("/b/locations", { headers: { "X-Business-Id": String(bid) } })
      .then((res) => {
        if (cancelled) return;
        const arr = Array.isArray(res?.data) ? res.data : [];
        const opts = [
          { value: "all", label: "Toate sucursalele" },
          ...arr.map((l) => ({
            value: String(l.id),
            label: l.name || l.title || `Locație #${l.id}`,
          })),
        ];

        setLocationOptions(opts);

        const locationIds = new Set(arr.map((l) => String(l.id)));
        const onlyLocationId =
          arr.length === 1 && arr[0]?.id != null ? String(arr[0].id) : "";
        const stored = window.localStorage.getItem(LS_LOCATION_SCOPE_KEY);
        const hasStoredScope = stored && locationIds.has(String(stored));

        if (onlyLocationId) {
          window.localStorage.setItem(LS_LOCATION_SCOPE_KEY, onlyLocationId);
          setLocationScope(onlyLocationId);
        } else if (stored && !opts.some((o) => o.value === String(stored))) {
          window.localStorage.removeItem(LS_LOCATION_SCOPE_KEY);
          setLocationScope("all");
        } else {
          setLocationScope(stored ? String(stored) : "all");
        }

        const currentLocationId = window.localStorage.getItem(LS_LOCATION_ID_KEY);

        if (onlyLocationId) {
          window.localStorage.setItem(LS_LOCATION_ID_KEY, onlyLocationId);
        } else if (hasStoredScope) {
          window.localStorage.setItem(LS_LOCATION_ID_KEY, String(stored));
        } else if (currentLocationId && locationIds.has(String(currentLocationId))) {
          window.localStorage.setItem(LS_LOCATION_ID_KEY, String(currentLocationId));
        } else if (currentLocationId && !locationIds.has(String(currentLocationId))) {
          window.localStorage.removeItem(LS_LOCATION_ID_KEY);
        }
      })
      .catch((e) => {
        if (cancelled) return;
        console.error("Eroare la încărcarea locațiilor:", e?.response?.data || e);
        setLocationOptions([{ value: "all", label: "Toate sucursalele" }]);
        setLocationScope("all");
      })
      .finally(() => {
        if (!cancelled) setLocationsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOwnerLike, currentBusinessId]);

  /* =========================
     Notifications (realtime via WebSocket)
  ========================= */
  const getBusinessIdSafe = () => {
    try {
      return currentBusinessId || window.localStorage.getItem("current_business_id") || null;
    } catch {
      return null;
    }
  };

  // keep latest soundEnabled in a ref (so WS handlers don't need re-connect)
  const soundEnabledRef = useRef(soundEnabled);
  useEffect(() => {
    soundEnabledRef.current = soundEnabled;
  }, [soundEnabled]);

  const wsRef = useRef(null);
  const wsRetryRef = useRef({ timer: null, attempt: 0 });

  const buildNotifWsUrl = (bid) => {
    const token = getAccessToken();
    if (!token) return null;

    const proto = window.location.protocol === "https:" ? "wss" : "ws";
    const host = window.location.host; // includes port
    return `${proto}://${host}/ws/b/${encodeURIComponent(String(bid))}/notifications/?token=${encodeURIComponent(
      token
    )}`;
  };

  // Fallback helpers (no polling): used on wake / dropdown open
  const fetchUnreadCount = async () => {
    const bid = getBusinessIdSafe();
    if (!bid) return;

    try {
      const { data } = await api.get("/b/notifications/unread-count", {
        headers: { "X-Business-Id": String(bid) },
      });

      const next = Number(data?.unread ?? 0) || 0;
      setUnreadCount(next);
      if (!unreadBootRef.current) {
        unreadBootRef.current = true;
        prevUnreadRef.current = next;
      } else {
        prevUnreadRef.current = next;
      }
    } catch {
      // silent
    }
  };

  const fetchNotifications = async () => {
    const bid = getBusinessIdSafe();
    if (!bid) return;

    setNotifLoading(true);
    try {
      const { data } = await api.get("/b/notifications", {
        headers: { "X-Business-Id": String(bid) },
        params: { limit: 25 },
      });
      setNotifItems(Array.isArray(data) ? data : []);
    } catch {
      setNotifItems([]);
    } finally {
      setNotifLoading(false);
    }
  };

  const applyUnreadSideEffects = (nextUnread) => {
    // first load: no sound
    if (!unreadBootRef.current) {
      unreadBootRef.current = true;
      prevUnreadRef.current = nextUnread;
      return;
    }

    if (nextUnread > prevUnreadRef.current) {
      if (soundEnabledRef.current) playNotifSound();
      try {
        window.dispatchEvent(new CustomEvent("appointments:refetch"));
        window.dispatchEvent(new CustomEvent("notifications:new"));
      } catch {}
    }

    prevUnreadRef.current = nextUnread;
  };

  const markNotifRead = async (notifId) => {
    const bid = getBusinessIdSafe();
    if (!bid || !notifId) return;

    try {
      await api.post(`/b/notifications/${notifId}/read`, null, {
        headers: { "X-Business-Id": String(bid) },
      });

      const nowIso = new Date().toISOString();
      setNotifItems((prev) =>
        (prev || []).map((n) => (n.id === notifId ? { ...n, read_at: n.read_at || nowIso } : n))
      );
      setUnreadCount((c) => Math.max(0, (Number(c) || 0) - 1));
    } catch {}
  };

  const markAppointmentStatus = async (e, notif, newStatus) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    const bid = getBusinessIdSafe();
    const apptId = notif?.payload?.appointment_id;
    if (!bid || !apptId) return;
    try {
      await api.patch(`/b/appointments/${apptId}`, { status: newStatus }, {
        headers: { "X-Business-Id": String(bid) },
      });
      if (!notif.read_at) markNotifRead(notif.id);
      // Actualizăm local notificarea ca citită + adăugăm status rezolvat
      // (backend-ul o va exclude din listă la următorul fetch, programarea nemaifiind 'booked')
      setNotifItems((prev) =>
        (prev || []).map((n) =>
          n.id === notif.id
            ? { ...n, read_at: n.read_at || new Date().toISOString(), _resolved: newStatus, _error: null }
            : n
        )
      );
      window.dispatchEvent(new CustomEvent("appointments:refetch"));
    } catch (err) {
      // Afișăm motivul în notificare — altfel click-ul pare că "nu face nimic"
      const d = err?.response?.data;
      let msg = "";
      if (d && typeof d === "object") {
        msg =
          (typeof d.detail === "string" && d.detail) ||
          Object.values(d)
            .flat()
            .filter((v) => typeof v === "string")
            .join(" ");
      }
      if (!msg) msg = "Nu am putut actualiza programarea. Încearcă din nou.";
      setNotifItems((prev) => (prev || []).map((n) => (n.id === notif.id ? { ...n, _error: msg } : n)));
    }
  };

  const markAllNotifsRead = async () => {
    const bid = getBusinessIdSafe();
    if (!bid) return;

    try {
      await api.post("/b/notifications/read-all", null, {
        headers: { "X-Business-Id": String(bid) },
      });

      const nowIso = new Date().toISOString();
      setNotifItems((prev) => (prev || []).map((n) => ({ ...n, read_at: n.read_at || nowIso })));
      setUnreadCount(0);
      prevUnreadRef.current = 0;
      unreadBootRef.current = true;
    } catch {}
  };

  // WebSocket connect (no polling)
  useEffect(() => {
    const bid = getBusinessIdSafe();
    const token = getAccessToken();
    if (!bid || !user?.id || !token) return;

    let alive = true;

    const cleanup = () => {
      try {
        const ws = wsRef.current;
        if (ws) {
          ws.onopen = null;
          ws.onmessage = null;
          ws.onerror = null;
          ws.onclose = null;
          ws.close();
        }
      } catch {}
      wsRef.current = null;

      try {
        const t = wsRetryRef.current?.timer;
        if (t) window.clearTimeout(t);
      } catch {}
      wsRetryRef.current.timer = null;
    };

    const scheduleReconnect = () => {
      if (!alive) return;
      const attempt = Math.min(8, (wsRetryRef.current.attempt || 0) + 1);
      wsRetryRef.current.attempt = attempt;
      const delay = Math.min(10000, 800 + attempt * 700);

      try {
        if (wsRetryRef.current.timer) window.clearTimeout(wsRetryRef.current.timer);
      } catch {}

      wsRetryRef.current.timer = window.setTimeout(() => {
        if (!alive) return;
        connect();
      }, delay);
    };

    const connect = () => {
      cleanup();
      const url = buildNotifWsUrl(bid);
      if (!url) return;

      try {
        const ws = new WebSocket(url);
        wsRef.current = ws;

        ws.onopen = () => {
          wsRetryRef.current.attempt = 0;
        };

        ws.onmessage = (ev) => {
          try {
            const msg = JSON.parse(ev.data || "{}");
            const t = msg?.type;

            if (t === "notif.boot") {
              const unread = Number(msg?.unread ?? 0) || 0;
              const items = Array.isArray(msg?.items) ? msg.items : [];
              setUnreadCount(unread);
              setNotifItems(items);
              unreadBootRef.current = true;
              prevUnreadRef.current = unread;
              return;
            }

            if (t === "notif.created") {
              const unread = Number(msg?.unread ?? 0) || 0;
              const n = msg?.notification;
              if (n && n.id) {
                setNotifItems((prev) => [n, ...(prev || []).filter((x) => x.id !== n.id)].slice(0, 25));
              }
              setUnreadCount(unread);
              applyUnreadSideEffects(unread);
              return;
            }

            if (t === "notif.read") {
              const id = msg?.id;
              const readAt = msg?.read_at || new Date().toISOString();
              const unread = Number(msg?.unread ?? 0) || 0;
              if (id) {
                setNotifItems((prev) =>
                  (prev || []).map((x) => (x.id === id ? { ...x, read_at: x.read_at || readAt } : x))
                );
              }
              setUnreadCount(unread);
              prevUnreadRef.current = unread;
              unreadBootRef.current = true;
              return;
            }

            if (t === "notif.read_all") {
              const readAt = msg?.read_at || new Date().toISOString();
              setNotifItems((prev) => (prev || []).map((x) => ({ ...x, read_at: x.read_at || readAt })));
              setUnreadCount(0);
              prevUnreadRef.current = 0;
              unreadBootRef.current = true;
            }
          } catch {
            // ignore
          }
        };

        ws.onclose = () => scheduleReconnect();
        ws.onerror = () => scheduleReconnect();
      } catch {
        scheduleReconnect();
      }
    };

    connect();

    return () => {
      alive = false;
      cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBusinessId, user?.id]);

  // Wake-up checks (tab focus / visibility) - fallback only, no intervals
  useEffect(() => {
    const onWake = () => {
      fetchUnreadCount();
      if (notifOpen) fetchNotifications();
    };

    window.addEventListener("focus", onWake);
    document.addEventListener("visibilitychange", onWake);

    return () => {
      window.removeEventListener("focus", onWake);
      document.removeEventListener("visibilitychange", onWake);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBusinessId, notifOpen]);

  // Open dropdown => load list once (no polling)
  useEffect(() => {
    if (notifOpen) fetchNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifOpen, currentBusinessId]);

  // Close notif dropdown on outside click / ESC
  useEffect(() => {
    if (!notifOpen) return;

    const onClick = (e) => {
      if (!notifMenuRef.current) return;
      if (!notifMenuRef.current.contains(e.target)) setNotifOpen(false);
    };

    const onKey = (e) => {
      if (e.key === "Escape") setNotifOpen(false);
    };

    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [notifOpen]);

  const formatNotifTime = (iso) => {
    try {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return "";
      return d.toLocaleString("ro-RO", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "";
    }
  };

  /* =========================
     Location scope change
  ========================= */
  const handleLocationScopeChange = (val) => {
    const v = val ? String(val) : "all";
    setLocationScope(v);

    try {
      if (v === "all") window.localStorage.removeItem(LS_LOCATION_SCOPE_KEY);
      else window.localStorage.setItem(LS_LOCATION_SCOPE_KEY, v);
    } catch {}

    // "Program de lucru" și unele acțiuni operaționale folosesc current_location_id.
    try {
      if (v !== "all") {
        window.localStorage.setItem("current_location_id", v);
      } else {
        const existing = window.localStorage.getItem("current_location_id");
        if (!existing) {
          const first = (locationOptions || []).find((o) => o.value !== "all");
          if (first?.value) {
            window.localStorage.setItem("current_location_id", String(first.value));
          }
        }
      }
    } catch {}

    try {
      window.dispatchEvent(
        new CustomEvent("location-scope:changed", {
          detail: { locationScopeId: v === "all" ? null : v },
        })
      );
    } catch {}
  };

  // helper: permisiuni pe baza codurilor de rol
  const hasRole = (requiredRoles) => {
    if (!requiredRoles || requiredRoles.length === 0) return true;
    if (!permissions) return false;

    const {
      isSuperAdmin,
      isOwnerCurrentBusiness,
      isDirectorCurrentBusiness,
      isReceptionCurrentBusiness,
      isEmployeeCurrentBusiness,
    } = permissions;

    return requiredRoles.some((code) => {
      switch (code) {
        case "owner":
          return isSuperAdmin || isOwnerCurrentBusiness;
        case "director":
          return isSuperAdmin || isOwnerCurrentBusiness || isDirectorCurrentBusiness;
        case "reception":
          return (
            isSuperAdmin ||
            isOwnerCurrentBusiness ||
            isDirectorCurrentBusiness ||
            isReceptionCurrentBusiness
          );
        case "employee":
          return (
            isSuperAdmin ||
            isOwnerCurrentBusiness ||
            isDirectorCurrentBusiness ||
            isReceptionCurrentBusiness ||
            isEmployeeCurrentBusiness
          );
        default:
          return false;
      }
    });
  };

  const canSeeDashboard = Boolean(permissions?.canSeeDashboard);
  const canSeeClients = Boolean(permissions?.canSeeClients);
  const canSeeStaff = Boolean(permissions?.canSeeStaff);

  // meniul principal din header (desktop)
  const primaryNavItems = [
    {
      label: "Dashboard",
      path: "/app/admin-dashboard",
      icon: "LayoutDashboard",
      requiredRoles: ["owner", "director"],
    },
    {
      label: "Calendar",
      path: "/app/appointment-calendar",
      icon: "Calendar",
      requiredRoles: ["owner", "director", "reception", "employee"],
    },
    {
      label: "Staff",
      path: "/app/staff-management",
      icon: "Users",
      requiredRoles: ["owner", "director"],
    },
    {
      label: "Clients",
      path: "/app/client-management",
      icon: "UserCheck",
      requiredRoles: ["owner", "director", "reception"],
    },
  ];

  let visiblePrimaryNavItems = primaryNavItems
    .filter((item) => !item.requiredRoles || hasRole(item.requiredRoles))
    .filter((item) => {
      if (item.path === "/app/admin-dashboard") return canSeeDashboard;
      if (item.path === "/app/staff-management") return canSeeStaff;
      if (item.path === "/app/client-management") return canSeeClients;
      return true;
    });

  // pentru angajatul "pur": doar Calendar în top-bar
  if (isPureEmployee) {
    visiblePrimaryNavItems = visiblePrimaryNavItems.filter(
      (item) => item.path === "/app/appointment-calendar"
    );
  }

  const handleUserMenuToggle = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setShowUserMenu((v) => !v);
  };

  useEffect(() => {
    if (!showUserMenu) return;

    const onPointerDown = (e) => {
      const el = userMenuRef.current;
      if (!el) return;
      if (el.contains(e.target)) return;
      setShowUserMenu(false);
    };

    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [showUserMenu]);

  const handleSignOut = (e) => {
    e?.preventDefault?.();
    logout();
    setShowUserMenu(false);
    navigate("/app/login", { replace: true });
  };

  const logoPath = isPureEmployee
    ? "/app/appointment-calendar"
    : canSeeDashboard
    ? "/app/admin-dashboard"
    : "/app/appointment-calendar";

  const showLocationScopeSelector = isOwnerLike && (locationOptions?.length || 0) > 2;

  const logoSrc = resolvedTheme === "dark" ? logoDark : logoLight;

  return (
    <header className="fixed top-0 left-0 right-0 bg-surface border-b border-border z-100" style={{ height: 'var(--header-total)', paddingTop: 'var(--sat)' }}>
      <div className="flex items-center justify-between h-16 px-2">
        {/* Left Section - Logo and Navigation */}
        <div className="flex items-center space-x-8">
          {/* Logo */}
          <Link to={logoPath} className="flex items-center space-x-3">
            <img
              src={logoSrc}
              alt="AppointIQ"
              className="h-10 w-auto object-contain ml-[1.5vw]"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {visiblePrimaryNavItems.map((item) => (
              <Button
                key={item.path}
                asChild
                variant="ghost"
                className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-text-secondary hover:text-foreground transition-colors duration-200"
              >
                <Link to={item.path}>
                  <span className="inline-flex items-center gap-2">
                    <Icon name={item.icon} size={16} />
                    {item.label}
                  </span>
                </Link>
              </Button>
            ))}
          </nav>
        </div>

        {/* Right Section - Tools + User Menu */}
        <div className="relative flex items-center space-x-4">
          {/* Owner-only: selector sucursală (scope de vizualizare) */}
          {showLocationScopeSelector && (
            <div className="hidden md:block min-w-[240px]">
              <Select
                options={locationOptions}
                value={locationScope}
                onChange={handleLocationScopeChange}
                placeholder="Toate sucursalele"
                loading={locationsLoading}
                clearable={false}
              />
            </div>
          )}

          {/* Notifications */}
          <div ref={notifMenuRef}>
            <Button
              variant="ghost"
              size="icon"
              className="relative"
              type="button"
              onClick={(e) => {
                e?.preventDefault?.();
                e?.stopPropagation?.();
                setNotifOpen((v) => !v);
              }}
            >
              <Icon name="Bell" size={20} />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-error text-white text-[10px] leading-[18px] text-center">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </Button>

            {notifOpen && (
              <div className="absolute top-full right-0 mt-2 w-[380px] max-w-[92vw] bg-popover border border-border rounded-lg shadow-elevation-2 z-200 overflow-hidden">
                <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-border">
                  <div className="text-sm font-medium text-popover-foreground">
                    Notificări
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={(e) => {
                        e?.preventDefault?.();
                        e?.stopPropagation?.();
                        toggleNotifSound();
                      }}
                      className="text-xs px-2 py-1 rounded-md text-primary hover:bg-muted"
                      title="Activează sunetul (pe mobil trebuie 1 tap)"
                    >
                      Sunet: {soundEnabled ? "On" : "Off"}
                    </button>

                    <button
                      type="button"
                      disabled={!unreadCount}
                      onClick={(e) => {
                        e?.preventDefault?.();
                        e?.stopPropagation?.();
                        markAllNotifsRead();
                      }}
                      className={`text-xs px-2 py-1 rounded-md ${
                        unreadCount
                          ? "text-primary hover:bg-muted"
                          : "text-muted-foreground cursor-not-allowed"
                      }`}
                    >
                      Marchează toate ca citite
                    </button>
                  </div>
                </div>

                <div className="max-h-[360px] overflow-auto">
                  {notifLoading ? (
                    <div className="p-3 text-sm text-muted-foreground">Se încarcă…</div>
                  ) : Array.isArray(notifItems) && notifItems.length ? (
                    notifItems.map((n) => {
                      const isUnread = !n.read_at;
                      return (
                        <button
                          key={n.id}
                          type="button"
                          onClick={(e) => {
                            e?.preventDefault?.();
                            e?.stopPropagation?.();

                            if (isUnread) markNotifRead(n.id);

                            const apptId = n?.payload?.appointment_id;
                            const at = n?.payload?.start_at;

                            if (apptId) {
                              const qs = new URLSearchParams({
                                appointment_id: String(apptId),
                                ...(at ? { at: String(at) } : {}),
                              }).toString();
                              navigate(`/app/appointment-calendar?${qs}`);
                            } else {
                              navigate("/app/appointment-calendar");
                            }

                            setNotifOpen(false);
                          }}
                          className={`w-full text-left px-3 py-2 border-b border-border last:border-b-0 hover:bg-muted transition-colors ${
                            isUnread ? "bg-muted/40" : ""
                          }`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-foreground truncate">
                                {n.title || "Notificare"}
                              </div>
                              {n.body ? (
                                <div className="text-xs text-text-secondary mt-1 whitespace-normal break-words">
                                  {n.body}
                                </div>
                              ) : null}
                              <div className="text-[11px] text-muted-foreground mt-1">
                                {formatNotifTime(n.created_at)}
                              </div>
                            </div>
                            {isUnread ? (
                              <span className="mt-1 w-2 h-2 rounded-full bg-primary flex-shrink-0" />
                            ) : null}
                          </div>
                          {n.type === "appointment_needs_status" && !n._resolved && (
                            <div className="flex gap-2 mt-2">
                              <button
                                type="button"
                                onClick={(e) => markAppointmentStatus(e, n, "completed")}
                                className="flex-1 text-xs px-2 py-1 rounded-md bg-green-500 hover:bg-green-600 text-white font-medium transition-colors"
                              >
                                Finalizat
                              </button>
                              <button
                                type="button"
                                onClick={(e) => markAppointmentStatus(e, n, "cancelled")}
                                className="flex-1 text-xs px-2 py-1 rounded-md bg-destructive hover:bg-destructive/90 text-destructive-foreground font-medium transition-colors"
                              >
                                Anulat
                              </button>
                            </div>
                          )}
                          {n.type === "appointment_needs_status" && !n._resolved && n._error && (
                            <div className="mt-1 text-xs text-destructive whitespace-normal break-words">
                              {n._error}
                            </div>
                          )}
                          {n.type === "appointment_needs_status" && n._resolved && (
                            <div className="mt-2 text-xs text-muted-foreground italic">
                              Marcat ca {n._resolved === "completed" ? "finalizat" : "anulat"} ✓
                            </div>
                          )}
                        </button>
                      );
                    })
                  ) : (
                    <div className="p-3 text-sm text-muted-foreground">
                      N-ai notificări încă.
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* User Menu */}
          <div ref={userMenuRef}>
            <Button
              variant="ghost"
              onClick={handleUserMenuToggle}
              className="flex items-center space-x-2 px-3 py-2"
              type="button"
            >
              <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                <Icon name="User" size={16} color="white" />
              </div>
              <div className="hidden md:block text-left">
                <div className="text-sm font-medium text-foreground">{displayName}</div>
                <div className="text-xs text-text-secondary">{primaryRole}</div>
              </div>
              <Icon name="ChevronDown" size={16} className="text-text-secondary" />
            </Button>

            {showUserMenu && (
              <div className="absolute top-full right-0 mt-1 w-56 bg-popover border border-border rounded-lg shadow-elevation-2 py-1 z-200">
                <div className="px-3 py-2 border-b border-border">
                  <div className="text-sm font-medium text-popover-foreground">{displayName}</div>
                  <div className="text-xs text-muted-foreground">{displayEmail}</div>
                  {primaryRole && (
                    <div className="text-xs text-muted-foreground mt-1">{primaryRole}</div>
                  )}
                </div>

                {/* Linkuri suplimentare doar dacă NU este angajat "pur" */}
                {!isPureEmployee && (
                  <>
                    <Button asChild variant="ghost" className="w-full justify-start px-3 py-2 text-sm">
                      <Link to="/app/profile" onClick={() => setShowUserMenu(false)}>
                        <Icon name="User" size={16} />
                        <span className="ml-3">Profile</span>
                      </Link>
                    </Button>

                    {canManageBusinessSettings && (
                      <Button
                        asChild
                        variant="ghost"
                        className="w-full justify-start px-3 py-2 text-sm"
                      >
                        <Link to="/app/business/settings" onClick={() => setShowUserMenu(false)}>
                          <Icon name="Building" size={16} />
                          <span className="ml-3">Business Settings</span>
                        </Link>
                      </Button>
                    )}

                    <Button
                      variant="ghost"
                      className="w-full justify-start px-3 py-2 text-sm"
                      type="button"
                      onClick={() => {
                        toggleThemePreference();
                        setShowUserMenu(false);
                      }}
                    >
                      <Icon name={resolvedTheme === "dark" ? "Sun" : "Moon"} size={16} />
                      <span className="ml-3">
                        {resolvedTheme === "dark" ? "Light Mode" : "Dark Mode"}
                        {themePref === "system" ? " (System)" : ""}
                      </span>
                    </Button>
                  </>
                )}

                <div className="px-3 py-2 border-t border-border">
                  <div className="flex flex-wrap gap-1">
                    {SUPPORTED_LANGUAGES.map((lang) => {
                      const isActive = i18n.language === lang.code || i18n.language?.startsWith(lang.code);
                      return (
                        <button
                          key={lang.code}
                          type="button"
                          onClick={() => {
                            i18n.changeLanguage(lang.code);
                            setShowUserMenu(false);
                          }}
                          title={lang.label}
                          className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs transition-colors ${
                            isActive
                              ? "bg-primary text-primary-foreground font-semibold"
                              : "hover:bg-muted text-muted-foreground"
                          }`}
                        >
                          <span>{lang.flag}</span>
                          <span>{lang.code.toUpperCase()}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="border-t border-border mt-1 pt-1">
                  <button
                    onClick={handleSignOut}
                    className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-error hover:bg-muted transition-colors duration-150"
                    type="button"
                  >
                    <Icon name="LogOut" size={16} />
                    <span>Sign Out</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Programări trecute fără status: fereastră blocantă până sunt marcate (portal pe body) */}
      <UnresolvedAppointmentsGate />
    </header>
  );
};

export default Header;
